/**
 * Formatting helpers for the events + clubs redesign. System voice: money is `$X.XX`
 * in mono, capacity is `28/40`, time is `SAT MAR 14, 8:00 PM`. Everything uppercase
 * that the system says; human content stays sentence case.
 */

const MINUS = '−';

export function money(cents, { whole = false, signed = false } = {}) {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  const n = Number(cents);
  const abs = Math.abs(n);
  const dollars = abs / 100;
  const useWhole = whole && Number.isInteger(dollars);
  const body = useWhole
    ? dollars.toLocaleString('en-US')
    : dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n < 0) return `${MINUS}$${body}`;
  if (signed && n > 0) return `+$${body}`;
  return `$${body}`;
}

/** `$1,840` when a whole-dollar figure reads better (goals, budgets). */
export function moneyWhole(cents) {
  if (cents === null || cents === undefined) return '—';
  const dollars = Math.round(Number(cents) / 100);
  return `$${dollars.toLocaleString('en-US')}`;
}

function d(iso) {
  if (!iso) return null;
  const dt = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const up = (s) => (s || '').toUpperCase();

/** `SAT MAR 14, 8:00 PM` */
export function whenLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  const day = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
  const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return up(`${day}, ${time}`);
}

/** `SAT MAR 14` */
export function dayLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  return up(dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', ''));
}

/** `MAR 14` */
export function dateLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  return up(dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
}

/** `MAR 14, 2026` */
export function dateYearLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  return up(dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
}

/** `MAR 2026` */
export function monthYearLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  return up(dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
}

/** `8:00 PM` */
export function timeLabel(iso) {
  const dt = d(iso);
  if (!dt) return '';
  return up(dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
}

/** `FRI JUL 10 – SAT JUL 11` (one day → `FRI JUL 10`). */
export function dayRangeLabel(startIso, endIso) {
  const a = dayLabel(startIso);
  const b = dayLabel(endIso);
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
}

/** `12:00 PM – 5:30 PM` (no end → `12:00 PM`). */
export function timeRangeLabel(startIso, endIso) {
  const a = timeLabel(startIso);
  const b = timeLabel(endIso);
  if (!b) return a;
  return `${a} – ${b}`;
}

/** Relative age in the system voice: `2H`, `5H`, `YESTERDAY`, `2 DAYS AGO`, then `MAR 4`. */
export function ago(iso, now = new Date()) {
  const dt = d(iso);
  if (!dt) return '';
  const diffMs = now.getTime() - dt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'JUST NOW';
  if (mins < 60) return `${mins}M`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}H`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'YESTERDAY';
  if (days < 7) return `${days} DAYS AGO`;
  return dateLabel(dt);
}

/** Lower-case human variant for meta lines: `accepted 41m ago`. */
export function agoLower(iso, now = new Date()) {
  return ago(iso, now).toLowerCase();
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Countdown text. style:
 *  - 'short'  → `2D 04H`, `04H 12M`, `12M`
 *  - 'long'   → `4D 11H 02M`, `11H 02M`, `02M`
 *  - 'clock'  → `18:42` (MM:SS) or `1:18:42` past an hour
 * Returns { text, expired, ms }.
 */
export function countdown(targetIso, { style = 'short', now = new Date() } = {}) {
  const dt = d(targetIso);
  if (!dt) return { text: '', expired: true, ms: 0 };
  const ms = dt.getTime() - now.getTime();
  if (ms <= 0) return { text: '', expired: true, ms: 0 };
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  let text;
  if (style === 'clock') {
    const h = days * 24 + hours;
    text = h > 0 ? `${h}:${pad2(mins)}:${pad2(secs)}` : `${pad2(mins)}:${pad2(secs)}`;
  } else if (style === 'long') {
    text = days > 0 ? `${days}D ${pad2(hours)}H ${pad2(mins)}M` : hours > 0 ? `${pad2(hours)}H ${pad2(mins)}M` : `${pad2(mins)}M`;
  } else {
    text = days > 0 ? `${days}D ${pad2(hours)}H` : hours > 0 ? `${pad2(hours)}H ${pad2(mins)}M` : `${Math.max(mins, 1)}M`;
  }
  return { text, expired: false, ms };
}

/** `28/40` */
export function capacityLabel(count, max) {
  if (!max) return String(count ?? 0);
  return `${count ?? 0}/${max}`;
}

export function plural(n, one, many = `${one}S`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Ticket noun for a CTA: `1 TICKET`, `3 TICKETS`, `A SEAT`, `2 SEATS`. */
export function ticketNoun(qty, noun = 'TICKET') {
  if (noun === 'SEAT') return qty > 1 ? `${qty} SEATS` : 'A SEAT';
  return qty > 1 ? `${qty} ${noun}S` : `1 ${noun}`;
}

/** Truncate for a crumb / title bar. */
export function crumb(text, max = 48) {
  const s = up(text || '');
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}
